import React, { useMemo, useRef, useEffect, useState } from 'react';
import '../../utils/agGridSetup';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridOptions } from 'ag-grid-community';
import { ChevronLeftIcon, ChevronRightIcon,  Edit, Trash2 } from 'lucide-react';
import { AlertData } from '../../services/alertApi';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import '../../styles/pages/DeviceTableView.css';
// import { options } from '../../utils/helperFunctions';
import { useNavigate } from 'react-router-dom';

interface AlertsTableProps {
  alerts: AlertData[];
  onEdit: (_alert: AlertData) => void;
  onDelete: (_alert: AlertData) => void;
  onStatusToggle: (_alertId: number) => void;
  searchTerm?: string;
}

const AlertsTable: React.FC<AlertsTableProps> = ({
  alerts,
  onEdit,
  onDelete,
  onStatusToggle,
  searchTerm = ''
}) => {
  const navigate = useNavigate();
  // Grid reference for API calls
  const gridRef = useRef<AgGridReact>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const goToPage = (page: number) => {
    if (gridRef.current?.api) {
      gridRef.current.api.paginationGoToPage(page);
      setCurrentPage(page);
    }
  };

  // Apply search filter when searchTerm prop changes
  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.setGridOption('quickFilterText', searchTerm);
    }
  }, [searchTerm]);

  // Column definitions
  const columnDefs: ColDef[] = useMemo(() => [
    {
      headerName: 'Alert Name',
      field: 'alertName',
      flex: 1,
      sortable: true,
      filter: false,
      wrapText: true,
    },
    {
      headerName: 'Device Name',
      field: 'deviceName',
      flex: 1.5,
      wrapText: true,
      sortable: true,
      filter: false,
      cellRenderer: (params: any) => {
        const device = params.data;
        return (
            <span 
              className="text-[#005EE5] dark:text-[#A9CCFE] hover:underline cursor-pointer"
              onClick={() => navigate(`/devices/${device.deviceId}`)}
            >
              {device.deviceName || 'Unnamed Device'}
            </span>
        );
      },
    },
    {
      headerName: 'Condition',
      field: 'alertConditions',
      cellRenderer: (params: any) => {
        const conditions = params.value;
        if (!conditions || conditions.length === 0) {
          return <span className="text-gray-500">No conditions</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {conditions.map((condition: any, idx: number) => {
              const parameterName = condition.pollutantParameterName || 
                                   condition.parameterName || 
                                   `Parameter ${condition.pollutantParameterId}`;
              const unit = condition.parameterUnitName || 
                          condition.unitSymbol || 
                          '';
              return (
                <span key={idx} className="condition-tag">
                  {parameterName} {condition.operator} {condition.thresholdValue} {unit}
                </span>
              );
            })}
          </div>
        );
      },
      flex: 2.5,
      sortable: false,
      filter: false,
      wrapText: true,       // allows text to wrap
      autoHeight: true,     // adjusts row height automatically
      cellStyle: { whiteSpace: 'normal' }
    },
    // {
    //   headerName: 'Recipients',
    //   field: 'alertRecipients',
    //   cellRenderer: (params: any) => {
    //     const recipients = params.value;
    //     if (!recipients || recipients.length === 0) {
    //       return <span className="text-gray-500">No recipients</span>;
    //     }
    //     return recipients.map((r: any) => r.email).join(', ');
    //   },
    //   flex: 1.5,
    //   sortable: false,
    //   filter: false,
    //   wrapText: true,
    // },
    {
      headerName: 'Status',
      field: 'isActive',
      cellRenderer: (params: any) => {
        const isActive = params.value;
        return (
          <div className="flex items-center gap-2">
            <div className="status-toggle-wrapper">
              <button
                onClick={() => onStatusToggle(params.data.alertId)}
                className={`status-toggle ${isActive ? 'active' : 'inactive'}`}
              >
                <div className="toggle-slider"></div>
              </button>
              <span className="status-tooltip">{isActive ? 'Active' : 'Inactive'}</span>
            </div>
            {/* <span className={`status-text ${isActive ? 'active' : 'inactive'}`}>
              {isActive ? 'Active' : 'Inactive'}
            </span> */}
          </div>
        );
      },
      flex: 1.25,
      sortable: true,
      filter: false,
    },
    // {
    //   headerName: 'Last Triggered',
    //   field: 'lastTriggeredAt',
    //   cellRenderer: (params: any) => {
    //     console.log(params);
    //     const lastTriggered = params.value;
    //     if (!lastTriggered) {
    //       return <span>--</span>;
    //     }
    //     const date = new Date(lastTriggered+'Z'); 
    //     return <span>{date.toLocaleString('en-US', options)}</span>;
    //   },
    //   flex: 1.5,
    //   sortable: true,
    //   filter: false,
    //   resizable: true,
    // },
    {
      headerName: 'Actions',
      field: 'actions',
      cellRenderer: (params: any) => {
        const alert = params.data;
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={() => onEdit(alert)}
              className="alerts-action-button edit"
              title="Edit Alert"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDelete(alert)}
              className="alerts-action-button delete"
              title="Delete Alert"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        );
      },
      flex: 1,
      sortable: false,
      filter: false,
    },
  ], []);

  // Grid options
  const gridOptions: GridOptions = useMemo(() => ({
    defaultColDef: {
      resizable: false,
      minWidth: 150,
      sortable: true,
      filter: false,
    },
    rowData: alerts,
    columnDefs: columnDefs,
    pagination: true,
    paginationPageSize: 10,
    suppressPaginationPanel: true,
    suppressCellFocus: true,
    suppressRowClickSelection: true,
    suppressRowDeselection: true,
    suppressDragLeaveHidesColumns: true,
    suppressColumnMoveAnimation: true,
    domLayout: 'normal',
    getRowHeight: (params: any) => {
      if (params.data.alertConditions.length <= 4) return 60;
      if (params.data.alertConditions.length <= 8) return 100;
      if (params.data.alertConditions.length <= 12) return 140;
      if (params.data.alertConditions.length <= 16) return 180;
      return 220;
    },
    overlayNoRowsTemplate: '<span class="custom-no-rows">Create your alerts to start monitoring AQI and pollutant levels.</span>',
    headerHeight: 75,
    icons: {
      sortAscending: '<span style="font-size:12px;">▲</span>',
      sortDescending: '<span style="font-size:12px;">▼</span>',
    },
    onGridReady: () => {
      gridRef.current?.api?.resetRowHeights();
    },
  }), [columnDefs, alerts]);

  return (
    <div className="ag-theme-alpine w-full device-view-container zoomOutLevel">
      <AgGridReact
        ref={gridRef}
        {...gridOptions}
        className="device-table-grid alerts-table-grid"
        onPaginationChanged={() => {
          if (gridRef.current?.api) {
            setCurrentPage(gridRef.current.api.paginationGetCurrentPage());
            setTotalPages(gridRef.current.api.paginationGetTotalPages());
          }
        }}
      />

      {totalPages > 1 && <div className='pagination-container h-auto'>
        <button 
          disabled={currentPage <= 0} 
          className='pagination-button pagination-previous' 
          onClick={() => goToPage(currentPage - 1)}
        >
          <ChevronLeftIcon className='w-4 h-4' />
        </button>

        {Array.from({ length: totalPages }, (_, i) => (
          <button
            key={i}
            onClick={() => goToPage(i)}
            className={`pagination-button ${currentPage === i ? 'active' : ''}`}
          >
            {i + 1}
          </button>
        ))}

        <button 
          disabled={currentPage >= totalPages - 1} 
          className='pagination-button pagination-next' 
          onClick={() => goToPage(currentPage + 1)}
        >
          <ChevronRightIcon className='w-4 h-4' />
        </button>
      </div>}
    </div>
  );
};

export default AlertsTable;
